"use client";

import { useLazyQuery, useMutation } from "@apollo/client";
import { useMsal } from "@azure/msal-react";
import {
  NO_ORGANIZATION_REDIRECT,
  RECOVERY_PASSWORD_WITH_TOKEN,
  REFRESH_TOKEN_PERSIST,
  USER_LANG,
  USER_TOKEN_PERSIST
} from "@constants/global";
import Microsoft from "@public/svg/microsoft";
import PasswordInput from "components/PasswordInput";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { useVendor } from "context/vendor/vendor.provider";
import { gqlCoins, gqlUser } from "gql";
import {
  IMicrosoftLoginResponse,
  ISocialNetworkLogInInput,
  ISocialNetworkLogInRes
} from "gql/User/mutations";
import setLanguage from "next-translate/setLanguage";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { isEmail } from "react-multi-email";
import { PUBLIC_ROUTES } from "routes/routes";
import { Input } from "rsuite";
import { Button } from "components/ui/v2";
import { forceRefetchQueries } from "settings/apollo";
import { IUser } from "types/User.types";
import { getQueryOperator } from "utils/helpers";
import LinkedInButton from "./LinkedIn";

interface ILoginForm {
  callback?: (error?: string) => void;
  redirect?: string;
  invitationCode?: string;
}

const LoginForm = (props: ILoginForm) => {
  const router = useRouter();
  const { t } = useTranslation("common");
  const { callback, invitationCode, redirect } = props;
  const { instance } = useMsal();

  const { vendor } = useVendor();

  // Microsoft login state management
  const [microsoftLoginState, setMicrosoftLoginState] = useState(() => {
    // Check immediately on mount if Microsoft auth is in progress
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const hasAuthCode = urlParams.has("code") || urlParams.has("state");
      const isMicrosoftAuthInProgress =
        sessionStorage.getItem("microsoftAuthInProgress") === "true";

      if (hasAuthCode || isMicrosoftAuthInProgress) {
        return {
          isProcessing: true,
          isRedirecting: false,
          error: null as string | null,
          timeoutId: null as NodeJS.Timeout | null
        };
      }
    }

    return {
      isProcessing: false,
      isRedirecting: false,
      error: null as string | null,
      timeoutId: null as NodeJS.Timeout | null
    };
  });

  const { setRefreshToken, setAccessToken, setUser, setBasketProducts } =
    useUserStoreContext((store) => ({
      setRefreshToken: store.setRefreshToken,
      setAccessToken: store.setAccessToken,
      setUser: store.setUser,
      setBasketProducts: store.setBasketProducts
    }));

  const [getUserBasket] = useLazyQuery(gqlCoins.queries.USER_BASKET, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
    onCompleted: ({ basket }) => {
      const { basketMemory } = basket;
      setBasketProducts(
        basketMemory.map(({ product, quantity }) => ({
          product,
          quantity
        })) || []
      );
    }
  });

  const [loginWithMicrosoft, { loading }] = useMutation<
    { loginWithMicrosoft: IMicrosoftLoginResponse },
    { microsoftLogInInput: ISocialNetworkLogInInput }
  >(gqlUser.mutations.LOGIN_WITH_MICROSOFT, {
    refetchQueries: [getQueryOperator(gqlUser.queries.GET_FULL_USER)],
    awaitRefetchQueries: true,
    onCompleted({ loginWithMicrosoft }) {
      // Clear the flag to allow SiteLoader to proceed
      sessionStorage.removeItem("microsoftAuthInProgress");

      setMicrosoftLoginState((prev) => ({
        ...prev,
        isProcessing: false,
        isRedirecting: false,
        error: null
      }));

      onCompletedLogin(loginWithMicrosoft);

      if (callback) {
        callback();
        return;
      }

      // * if the user came from an invitation link, redirect to inv
      if (invitationCode) {
        router.push({
          pathname: PUBLIC_ROUTES.invitation.path,
          query: {
            t: invitationCode,
            redirect: redirect as string
          }
        });
        return;
      }
    },
    onError(error) {
      console.error("Microsoft login mutation failed:", error);

      // Clear the flag on error
      sessionStorage.removeItem("microsoftAuthInProgress");

      setMicrosoftLoginState((prev) => ({
        ...prev,
        isProcessing: false,
        isRedirecting: false,
        error: t(
          "Error al iniciar sesión con Microsoft. Por favor, inténtalo de nuevo."
        )
      }));
    }
  });

  const handleMicrosoftLogin = async () => {
    try {
      // Set flag to prevent SiteLoader interference
      sessionStorage.setItem("microsoftAuthInProgress", "true");

      setMicrosoftLoginState((prev) => ({
        ...prev,
        isRedirecting: true,
        error: null
      }));

      await instance.loginRedirect({
        prompt: "select_account",
        scopes: ["User.Read"]
      });
    } catch (error) {
      console.error("Microsoft login redirect failed:", error);
      sessionStorage.removeItem("microsoftAuthInProgress");
      setMicrosoftLoginState((prev) => ({
        ...prev,
        isRedirecting: false,
        error: t(
          "Error al conectar con Microsoft, Por favor, inténtalo de nuevo"
        )
      }));
    }
  };

  useEffect(() => {
    const handleMicrosoftRedirect = async () => {
      try {
        // Check if we have URL parameters that indicate we're returning from Microsoft
        const urlParams = new URLSearchParams(window.location.search);
        const hasAuthCode = urlParams.has("code") || urlParams.has("state");

        // Also check if sessionStorage indicates we're in Microsoft auth flow
        const isMicrosoftAuthInProgress =
          sessionStorage.getItem("microsoftAuthInProgress") === "true";

        if (hasAuthCode || isMicrosoftAuthInProgress) {
          setMicrosoftLoginState((prev) => ({
            ...prev,
            isProcessing: true,
            error: null
          }));
        }

        // Set timeout for the redirect promise
        const timeoutId = setTimeout(() => {
          setMicrosoftLoginState((prev) => ({
            ...prev,
            isProcessing: false,
            error: t(
              "La autenticación está tomando más tiempo del esperado. Por favor, inténtalo de nuevo."
            )
          }));
        }, 30000); // 30 seconds timeout

        setMicrosoftLoginState((prev) => ({ ...prev, timeoutId }));

        const tokenResponse = await instance.handleRedirectPromise();

        // Clear timeout if we get a response
        clearTimeout(timeoutId);

        if (tokenResponse) {
          await loginWithMicrosoft({
            variables: {
              microsoftLogInInput: {
                accessToken: tokenResponse?.accessToken
              }
            }
          });
        } else if (!hasAuthCode && !isMicrosoftAuthInProgress) {
          // No token response and no auth indicators means user didn't complete auth or page was refreshed normally
          setMicrosoftLoginState((prev) => ({
            ...prev,
            isProcessing: false
          }));
        }
      } catch (error) {
        console.error("Microsoft redirect handling failed:", error);
        setMicrosoftLoginState((prev) => ({
          ...prev,
          isProcessing: false,
          error: t(
            "Error durante la autenticación con Microsoft. Por favor, inténtalo de nuevo."
          )
        }));
      }
    };

    handleMicrosoftRedirect();

    // Cleanup timeout on unmount
    return () => {
      if (microsoftLoginState.timeoutId) {
        clearTimeout(microsoftLoginState.timeoutId);
      }
    };
  }, []);

  const [getUser, { data: getUserData }] = useLazyQuery<
    { user: IUser },
    { accessToken: string }
  >(gqlUser.queries.GET_FULL_USER, {
    fetchPolicy: "network-only",

    onCompleted: ({ user }) => {
      if (user && user.lang) {
        setLanguage(user.lang);
        localStorage.setItem(USER_LANG, user.lang);
      } else {
        setLanguage("en");
        localStorage.setItem(USER_LANG, "en");
      }

      setUser(user);

      if (user?.selectedVendor?.enableCoins) {
        getUserBasket();
      }
    }
  });

  const [login, { loading: loginLoading, error: loginError }] = useMutation(
    gqlUser.queries.LOGIN,
    {
      onCompleted({ login }) {
        onCompletedLogin(login);
      },
      onError({ message }) {
        if (callback) callback(message);
      }
    }
  );

  const onCompletedLogin = (login: ISocialNetworkLogInRes) => {
    const { accessToken, refreshToken } = login;

    setAccessToken(accessToken);
    setRefreshToken(refreshToken);

    localStorage.setItem(USER_TOKEN_PERSIST, accessToken);
    localStorage.setItem(REFRESH_TOKEN_PERSIST, refreshToken);

    getUser({
      variables: {
        accessToken
      }
    });
  };

  const {
    handleSubmit: handleLoginSubmit,
    control: loginControl,
    formState: { errors }
  } = useForm<{ email: string; password: string }>();

  const handleLogin: SubmitHandler<{
    email: string;
    password: string;
  }> = async ({ email, password }) => {
    try {
      const { data } = await login({
        variables: {
          loginInput: {
            email,
            password
          }
        }
      });

      if (data && data.login) {
        onCompletedLogin(data.login);
      }
    } catch (e) {
      console.error("Login error:", e);
    }
  };

  const onRecoverPassoword = () => {
    router.push(RECOVERY_PASSWORD_WITH_TOKEN);
  };

  const processUserData = async (userData) => {
    const {
      user: { vendorList }
    } = userData;

    if (callback) {
      callback();
      return;
    }

    // * if the user came from an invitation link, redirect to inv
    if (invitationCode) {
      router.push({
        pathname: PUBLIC_ROUTES.invitation.path,
        query: {
          t: invitationCode
        }
      });
      return;
    }

    await forceRefetchQueries([gqlUser.queries.GET_FULL_USER]);

    if (!vendorList?.length) {
      router.push(NO_ORGANIZATION_REDIRECT);
    }
  };

  useEffect(() => {
    if (getUserData) {
      processUserData(getUserData);
    }
  }, [getUserData]);

  return (
    <>
      {/* Microsoft Authentication Loading Overlay */}
      {microsoftLoginState.isProcessing && (
        <div className="dark:bg-gray-900/80 fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="rounded-lg bg-white p-8 shadow-xl dark:bg-gray-800">
            <div className="flex flex-col items-center space-y-4">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
              <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {t("Autenticando con Microsoft")}
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {t("Completando tu inicio de sesión...")}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-zinc-400">
                  {t("Esto puede tomar unos momentos")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleLoginSubmit(handleLogin)}>
        {!vendor?.onlyAdLogin && (
          <>
            <label className="font-input text-left font-semibold">
              {t("Ingresa tu correo electrónico")}
            </label>
            <Controller
              name="email"
              defaultValue=""
              control={loginControl}
              rules={{
                required: true,
                validate: (value) => isEmail(value)
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  className="mt-2"
                  placeholder={t("Ingresa tu correo electrónico")}
                />
              )}
            />
            {errors && errors.email && (
              <small className="w-full text-red-500">
                {t("Debes ingresar un e-mail válido")}
              </small>
            )}
            <label className="font-input mt-6 block text-left font-semibold">
              {t("Ingresa tu contraseña")}
            </label>
            <Controller
              name="password"
              defaultValue=""
              control={loginControl}
              rules={{ required: true }}
              render={({ field: { ...rest }, fieldState }) => (
                <PasswordInput
                  {...rest}
                  {...fieldState}
                  placeholder={t("Ingresa tu contraseña2")}
                />
              )}
            />
            {errors && errors.password && (
              <small className="w-full text-red-500">
                {t("Debes ingresar una contraseña")}
              </small>
            )}
            <div className="mt-4 flex items-center">
              <div
                className="font-input ml-auto"
                style={{ color: "#5A27E7", cursor: "pointer" }}
                onClick={onRecoverPassoword}
                aria-hidden
              >
                {t("No recuerdo mi contraseña")}
              </div>
            </div>
          </>
        )}

        <div className="mt-6 flex w-full flex-col items-center gap-2 md:mt-10">
          {!vendor?.onlyAdLogin && (
            <>
              <div className="flex w-full justify-center">
                <Button
                  className="w-screen p-4"
                  variant="primary"
                  loading={loginLoading}
                  type="submit"
                >
                  {t("Ingresar a tu cuenta")}
                </Button>
              </div>

              {vendor?.enableLoginWithLinkedin && (
                <LinkedInButton
                  onCompleted={onCompletedLogin}
                  parentLoading={loginLoading}
                />
              )}
            </>
          )}

          {vendor?.enableMicrosoftLogin && (
            <Button
              className="w-full rounded-lg border border-gray-300 bg-white p-4 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              variant="secondary"
              onClick={handleMicrosoftLogin}
              loading={
                loading ||
                microsoftLoginState.isProcessing ||
                microsoftLoginState.isRedirecting
              }
              disabled={
                loading ||
                microsoftLoginState.isProcessing ||
                microsoftLoginState.isRedirecting
              }
            >
              <div className="flex w-full items-center justify-center gap-3">
                {!(
                  loading ||
                  microsoftLoginState.isProcessing ||
                  microsoftLoginState.isRedirecting
                ) && <Microsoft />}
                <span className="font-medium">
                  {microsoftLoginState.isRedirecting
                    ? t("Redirigiendo a Microsoft")
                    : microsoftLoginState.isProcessing || loading
                      ? t("Procesando autenticación")
                      : t("Continuar con Microsoft")}
                </span>
              </div>
            </Button>
          )}
        </div>
        {loginError && (
          <p className="mt-4 w-full text-center text-red-500">
            {loginError.message}
          </p>
        )}

        {microsoftLoginState.error && (
          <div className="mt-4 w-full rounded-md bg-red-50 p-3 text-center">
            <p className="text-sm text-red-700">{microsoftLoginState.error}</p>
            <button
              onClick={() =>
                setMicrosoftLoginState((prev) => ({ ...prev, error: null }))
              }
              className="mt-2 text-xs text-red-600 underline hover:text-red-800"
            >
              {t("Cerrar")}
            </button>
          </div>
        )}

        {microsoftLoginState.isProcessing && (
          <div className="mt-4 w-full rounded-md bg-blue-50 p-3 text-center">
            <p className="text-sm text-blue-700">
              {t("Completando autenticación con Microsoft...")}
            </p>
            <p className="mt-1 text-xs text-blue-600">
              {t("Esto puede tomar unos momentos, por favor espera.")}
            </p>
          </div>
        )}
      </form>
    </>
  );
};

export default LoginForm;
